import React, { useEffect, useState } from "react";
import { MapPin, Phone, Mail, Clock, Send } from "lucide-react";
import { toast } from "sonner";
import { saveContactMessage } from "../data/mock";
import { useRevealGroup } from "../hooks/useReveal";
import {
  PageHero,
  PillBadge,
  CTASection,
} from "../components/Shared";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

const CONTACT = {
  name: "Abhi Insurance Agency Inc.",
  address: "8128 128 St #302",
  city: "Surrey, BC V3W 1R1",
  phone: "(604) 996 6862",
  phoneHref: "tel:+16049966862",
  email: "info@abhifinancial.com",
  emailHref: "mailto:info@abhifinancial.com",
  hours: "Open 7 days a week, 9:00 to 17:00",
};

const GOOGLE_MAP_EMBED_URL =
  "https://www.google.com/maps?q=8128%20128%20St%20%23302%2C%20Surrey%2C%20BC%20V3W%201R1%2C%20Canada&output=embed";

const GOOGLE_MAP_DIRECTIONS_URL =
  "https://www.google.com/maps/dir/?api=1&destination=8128%20128%20St%20%23302%2C%20Surrey%2C%20BC%20V3W%201R1%2C%20Canada";

const INSURANCE_TYPES = [
  "Super Visa",
  "Visitor Visa",
  "Life",
  "Critical Illness",
  "Disability",
  "Not sure",
];

const INITIAL_FORM = {
  name: "",
  email: "",
  phone: "",
  subject: "",
  message: "",
  consent: false,
};

const PAGE_DISCLAIMER =
  "Abhi Insurance Agency Inc. is a licensed insurance advisory based in Surrey, British Columbia. The information on this page is general in nature and is not financial, legal or insurance advice. Coverage terms, eligibility, premiums and program requirements vary by insurer and by individual circumstances, and change over time. Nothing here guarantees eligibility, approval or a particular premium. Speak with a licensed advisor before making any insurance decision, and refer to your policy documents for the terms that actually apply to you.";

  const MOBILE_PAGE_DISCLAIMER =
  "This information is general only and is not financial, legal or insurance advice. Coverage, eligibility and premiums vary. Speak with a licensed advisor before making an insurance decision.";

const INFO = [
  {
    icon: MapPin,
    title: CONTACT.name,
    lines: [CONTACT.address, CONTACT.city],
    cardHref: GOOGLE_MAP_DIRECTIONS_URL,
    newTab: true,
    ariaLabel:
      "Open Abhi Insurance Agency location in Google Maps",
  },
  {
    icon: Phone,
    title: "Phone",
    lines: [CONTACT.phone],
    cardHref: CONTACT.phoneHref,
    ariaLabel: `Call Abhi Insurance Agency at ${CONTACT.phone}`,
  },
  {
    icon: Mail,
    title: "Email",
    lines: [CONTACT.email],
    cardHref: CONTACT.emailHref,
    ariaLabel: `Email Abhi Insurance Agency at ${CONTACT.email}`,
  },
  {
    icon: Clock,
    title: "Working Hours",
    lines: [CONTACT.hours],
  },
];

function useContactMetadata() {
  useEffect(() => {
    const previousTitle = document.title;

    const existing = document.head.querySelector(
      'meta[name="description"]'
    );

    const previousDescription =
      existing?.getAttribute("content") ?? null;

    const meta = existing || document.createElement("meta");

    if (!existing) {
      meta.setAttribute("name", "description");
      document.head.appendChild(meta);
    }

    document.title =
      "Contact Abhi Insurance Agency Surrey BC | Call (604) 996 6862";

    meta.setAttribute(
      "content",
      "Speak with a licensed insurance advisor in Surrey BC. 8128 128 St #302. Free consultations 7 days a week, 9:00 to 17:00. Call (604) 996 6862."
    );

    return () => {
      document.title = previousTitle;

      if (!existing) {
        meta.remove();
      } else if (previousDescription === null) {
        meta.removeAttribute("content");
      } else {
        meta.setAttribute("content", previousDescription);
      }
    };
  }, []);
}

export default function Contact() {
  const ref = useRevealGroup();

  const [form, setForm] = useState({
    ...INITIAL_FORM,
  });

  const [submitting, setSubmitting] = useState(false);

  useContactMetadata();

  const set = (key, value) => {
    setForm((previous) => ({
      ...previous,
      [key]: value,
    }));
  };

  const handleSubmit = (event) => {
    event.preventDefault();

    if (submitting) return;

    if (!form.subject) {
      toast.error("Please choose a type of insurance.");
      return;
    }

    if (!form.consent) {
      toast.error("Please agree to the data collection consent.");
      return;
    }

    setSubmitting(true);

    setTimeout(async () => {
      try {
        await saveContactMessage(form);

        setForm({
          ...INITIAL_FORM,
        });

        toast.success("Message sent!", {
          description:
            "Thank you for reaching out. We'll follow up shortly.",
        });
      } catch {
        toast.error("Unable to save your message. Please try again.");
      } finally {
        setSubmitting(false);
      }
    }, 700);
  };

  return (
    <div ref={ref}>
      <PageHero
        badge="Contact Us"
        title="We'd Love To Hear From You"
        subtitle="Making sure you and your family are protected when the unexpected happens. Free consultations, 7 days a week."
        image="https://images.pexels.com/photos/7709287/pexels-photo-7709287.jpeg?auto=compress&cs=tinysrgb&w=2000"
        imageAlt="A friendly team ready to answer client questions"
      />

      {/* Location and contact information */}
      <section className="bg-[#F4F7FB] py-16">
        <div className="mx-auto max-w-7xl px-6">
          <div
            className="mx-auto max-w-2xl text-center"
            data-reveal
          >
            <h2 className="font-heading text-3xl font-extrabold leading-[1.15] tracking-tight text-[#0B2748] sm:text-4xl">
              Our{" "}
              <span className="text-[#0785C7]">
                Location
              </span>
            </h2>

            <p className="mt-4 text-[15.5px] leading-relaxed text-[#526A7D]">
              Abhi Insurance Agency meets all your insurance needs,
              including life insurance, visitor visa insurance,
              disability insurance and more.
            </p>
          </div>

          <div className="mt-9 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
  {INFO.map((card, index) => {
    const Icon = card.icon;
    const CardElement = card.cardHref ? "a" : "div";

    return (
      <CardElement
        key={card.title}
        href={card.cardHref}
        target={card.newTab ? "_blank" : undefined}
        rel={card.newTab ? "noopener noreferrer" : undefined}
        aria-label={card.ariaLabel}
        data-reveal
        style={{
          transitionDelay: `${index * 80}ms`,
        }}
        className={`group min-w-0 rounded-2xl border border-transparent bg-white p-8 text-center shadow-[0_2px_16px_rgba(0,111,185,0.07)] transition-all duration-500 hover:-translate-y-1.5 hover:shadow-[0_20px_44px_rgba(0,111,185,0.13)] focus:outline-none ${
          card.cardHref ? "cursor-pointer" : ""
        }`}
      >
        <span className="mx-auto grid h-[52px] w-[52px] place-items-center rounded-2xl bg-[#E2F2FA] transition-all duration-500 group-hover:rotate-6 group-hover:bg-[#006FB9]">
          <Icon className="h-6 w-6 text-[#006FB9] transition-colors duration-500 group-hover:text-[#B9DFF3]" />
        </span>

        <h3 className="mt-4 font-heading text-base font-bold text-[#0B2748]">
          {card.title}
        </h3>

        {card.lines.map((line) => (
          <p
            key={line}
            className="mt-1 break-words text-sm text-[#526A7D]"
          >
            {line}
          </p>
        ))}
      </CardElement>
    );
  })}
</div>
        </div>
      </section>

      {/* Contact form, map and aligned information panels */}
      <section className="bg-white py-20 lg:py-24">
        <div className="mx-auto grid max-w-7xl grid-cols-1 items-stretch gap-x-14 gap-y-6 px-6 lg:grid-cols-[1.2fr_1fr]">
          {/* Form */}
          <div
            className="order-1 min-w-0"
            data-reveal
          >
            <PillBadge>
              Request a Free Consultation
            </PillBadge>

            <h2 className="mt-4 font-heading text-3xl font-extrabold leading-[1.15] tracking-tight text-[#0B2748] sm:text-4xl">
              Send Us a{" "}
              <span className="text-[#0785C7]">
                Message
              </span>
            </h2>

            <p className="mt-4 text-[15.5px] leading-relaxed text-[#526A7D]">
              We would love to help you make the important decisions
              about your insurance. Fill in the form and an advisor
              will get back to you promptly.
            </p>

            <form
              onSubmit={handleSubmit}
              className="mt-9 space-y-5"
              aria-busy={submitting}
            >
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                <Input
                  required
                  name="name"
                  autoComplete="name"
                  aria-label="Name"
                  placeholder="Name"
                  value={form.name}
                  onChange={(event) =>
                    set("name", event.target.value)
                  }
                  className="h-12 rounded-xl border-[#DCE9F1]"
                  data-testid="contact-name"
                />

                <Input
                  required
                  type="email"
                  name="email"
                  autoComplete="email"
                  aria-label="Email"
                  placeholder="Email"
                  value={form.email}
                  onChange={(event) =>
                    set("email", event.target.value)
                  }
                  className="h-12 rounded-xl border-[#DCE9F1]"
                  data-testid="contact-email"
                />
              </div>

              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                <Input
                  required
                  type="tel"
                  name="phone"
                  autoComplete="tel"
                  aria-label="Phone"
                  placeholder="Phone"
                  value={form.phone}
                  onChange={(event) =>
                    set("phone", event.target.value)
                  }
                  className="h-12 rounded-xl border-[#DCE9F1]"
                  data-testid="contact-phone"
                />

                <Select
                  value={form.subject}
                  onValueChange={(value) =>
                    set("subject", value)
                  }
                >
                  <SelectTrigger
                    aria-label="Type of insurance"
                    aria-required="true"
                    className="h-12 w-full min-w-0 rounded-xl border-[#DCE9F1] max-sm:!justify-between max-sm:!text-left max-sm:[&>span]:!flex-1 max-sm:[&>span]:!text-left max-sm:[&>svg]:!ml-auto max-sm:[&>svg]:!shrink-0"
                    data-testid="contact-insurance-type"
                  >
                    <SelectValue placeholder="Type of insurance" />
                  </SelectTrigger>

                  <SelectContent>
                    {INSURANCE_TYPES.map((type) => (
                      <SelectItem key={type} value={type}>
                        {type}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <Textarea
                required
                name="message"
                aria-label="Message"
                placeholder="Message"
                value={form.message}
                onChange={(event) =>
                  set("message", event.target.value)
                }
                className="min-h-[140px] rounded-xl border-[#DCE9F1]"
                data-testid="contact-message"
              />

              <label className="flex cursor-pointer items-start gap-2.5 text-sm text-[#526A7D]">
                <Checkbox
                  checked={form.consent}
                  onCheckedChange={(value) =>
                    set("consent", value === true)
                  }
                  className="mt-0.5"
                  data-testid="contact-consent"
                />

                <span>
                  I agree that my submitted data is being collected
                  and stored. For further details on handling user
                  data, see our Privacy Policy.
                </span>
              </label>

              <button
                type="submit"
                disabled={submitting}
                className="btn-primary group"
                data-testid="contact-submit"
              >
                {submitting ? "Sending…" : "Send Message"}

                <Send className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </button>
            </form>
          </div>

          {/* Google Map */}
          <div
            className="order-3 min-h-[430px] overflow-hidden rounded-3xl border border-[#E1ECF3] bg-[#F4F7FB] shadow-[0_12px_35px_rgba(6,59,103,0.10)] lg:order-2 lg:h-full lg:min-h-[560px]"
            data-reveal
            style={{
              transitionDelay: "120ms",
            }}
          >
            <iframe
              src={GOOGLE_MAP_EMBED_URL}
              title="Abhi Insurance Agency location"
              className="h-full min-h-[430px] w-full border-0 lg:min-h-[560px]"
              loading="lazy"
              allowFullScreen
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>

          {/* Call panel */}
          <div
            className="contact-call-panel order-2 h-full rounded-3xl bg-[#006FB9] p-7 text-white sm:p-9 lg:order-3"
            data-reveal
          >
            <h3 className="font-heading text-xl font-bold">
              Prefer to talk right away?
            </h3>

            <p className="mt-3 text-sm leading-relaxed text-[#C5DEEC]">
              Our advisors are available 7 days a week.
            </p>

            <a
              href={CONTACT.phoneHref}
              className="btn-light mt-6 w-fit"
              data-testid="contact-call-now"
            >
              <Phone className="h-4 w-4 shrink-0" />
              Call {CONTACT.phone}.
            </a>
          </div>

          {/* Service area panel */}
          <div
            className="order-4 h-full rounded-3xl border border-[#E1ECF3] p-7 sm:p-9"
            data-reveal
            style={{
              transitionDelay: "120ms",
            }}
          >
            <h3 className="font-heading text-lg font-bold text-[#0B2748]">
              Serving Surrey and the Lower Mainland
            </h3>

            <p className="mt-3 text-sm leading-relaxed text-[#526A7D]">
              Our office is on 128 St in Surrey, and we help clients
              across Newton, Fleetwood, Guildford, Cloverdale,
              South Surrey, Whalley and City Centre, as well as
              Delta, Langley, White Rock, Burnaby, Richmond,
              Coquitlam and Vancouver. In person and virtual
              consultations both available.
            </p>

            <a
              href={GOOGLE_MAP_DIRECTIONS_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-5 inline-flex items-start gap-1.5 text-sm font-bold text-[#006FB9] transition-colors hover:text-[#005A96] hover:underline"
            >
              <MapPin className="mt-0.5 h-4 w-4 shrink-0" />

              <span>
                {CONTACT.address}, {CONTACT.city}
              </span>
            </a>
          </div>
        </div>
      </section>

      <CTASection
        title="Your Protection Starts With a Conversation"
        subtitle="Free consultations 7 days a week. No hidden fees. Your satisfaction is 100% guaranteed."
      />

     <section
  aria-label="Service and page disclaimer"
  className="bg-[#F4F7FB] py-8 lg:py-10"
>
  <div className="mx-auto max-w-7xl px-6">
    <div className="rounded-xl border-l-[5px] border-r-[5px] border-[#0B4FAF] bg-white px-5 py-5 shadow-[0_8px_24px_rgba(11,39,72,0.08)] sm:px-7 lg:px-8">
      
      {/* Short disclaimer for mobile */}
      <p className="text-sm leading-relaxed text-[#526A7D] md:hidden">
        {MOBILE_PAGE_DISCLAIMER}
      </p>

      {/* Complete disclaimer for tablet and desktop */}
      <p className="hidden text-sm leading-relaxed text-[#526A7D] md:block">
        {PAGE_DISCLAIMER}
      </p>
      
    </div>
  </div>
</section>
    </div>
  );
}